import { FormEvent, useState } from "react";
import { MapPin } from "lucide-react";
import * as devicesApi from "../../api/devices";
import { DevicePatch } from "../../api/devices";
import { CassaforteMagazzino } from "../../api/magazzino";
import { getErrorMessage } from "../../api/client";
import { useToast } from "../../context/ToastContext";
import { Modal } from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { SedeAutocomplete } from "../../components/ui/SedeAutocomplete";
import { etichettaMatricola } from "../../utils/matricola";

/**
 * Sede, consegna prevista e — se manca — matricola di una cassaforte in
 * magazzino, direttamente dalla lista.
 *
 * Scrive con `PATCH /api/devices/:id`, la stessa rotta della scheda del
 * dispositivo: la sede è `devices.sede_id`, la posizione fisica, senza passare
 * da clienti o contratti. Assegnarla, cambiarla e toglierla (la X sulla sede)
 * sono la stessa operazione. Si mandano solo i campi cambiati.
 *
 * La matricola compare solo quando non c'è: da NULL a valore si può, dopo non
 * si cambia più (il backend risponde 409). Per questo il campo, una volta
 * salvato, sparisce invece di restare modificabile per finta.
 */
export function ModificaCassaforte({
  cassaforte,
  onChiudi,
  onFatto,
}: {
  cassaforte: CassaforteMagazzino;
  onChiudi: () => void;
  onFatto: () => void;
}) {
  const { showToast } = useToast();
  const sedeIniziale = cassaforte.sede_cliente;
  const [sedeTesto, setSedeTesto] = useState(
    sedeIniziale ? [sedeIniziale.nome_sede, sedeIniziale.citta].filter(Boolean).join(" · ") : ""
  );
  const [sedeId, setSedeId] = useState(sedeIniziale?.id ?? "");
  const [consegna, setConsegna] = useState(cassaforte.data_consegna_prevista?.slice(0, 10) ?? "");
  const [matricola, setMatricola] = useState("");
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const modello = cassaforte.dispositivo_catalogo
    ? `${cassaforte.dispositivo_catalogo.marca} ${cassaforte.dispositivo_catalogo.modello}`
    : "Cassaforte";

  async function salva(e: FormEvent) {
    e.preventDefault();
    const patch: DevicePatch = {};
    if ((sedeId || null) !== (sedeIniziale?.id ?? null)) patch.sede_id = sedeId || null;
    if ((consegna || null) !== (cassaforte.data_consegna_prevista?.slice(0, 10) ?? null)) {
      patch.data_consegna_prevista = consegna || null;
    }
    if (cassaforte.matricola === null && matricola.trim()) patch.matricola = matricola.trim();

    if (Object.keys(patch).length === 0) {
      onChiudi();
      return;
    }
    setInCorso(true);
    setErrore(null);
    try {
      await devicesApi.updateDevice(cassaforte.id, patch);
      showToast(`${modello} (${etichettaMatricola(patch.matricola ?? cassaforte.matricola)}) aggiornata.`, "success");
      onFatto();
    } catch (err) {
      setErrore(getErrorMessage(err, "Impossibile salvare la cassaforte."));
    } finally {
      setInCorso(false);
    }
  }

  return (
    <Modal
      open
      onClose={onChiudi}
      title={`${modello} — ${etichettaMatricola(cassaforte.matricola)}`}
      size="md"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onChiudi}>
            Annulla
          </Button>
          <Button type="submit" form="form-modifica-cassaforte" loading={inCorso}>
            <MapPin className="h-4 w-4" aria-hidden="true" />
            Salva
          </Button>
        </div>
      }
    >
      <form id="form-modifica-cassaforte" onSubmit={salva} className="flex flex-col gap-4" noValidate>
        <SedeAutocomplete
          label="Sede"
          placeholder="Nessuna — cerca per insegna, nome o città"
          value={sedeTesto}
          onChange={setSedeTesto}
          sedeId={sedeId}
          onSedeIdChange={setSedeId}
          soloSelezione
        />

        <Input
          type="date"
          label="Consegna prevista"
          hint="Arrivo presunto in magazzino dal fornitore. Vuota = non nota."
          value={consegna}
          onChange={(e) => setConsegna(e.target.value)}
        />

        {cassaforte.matricola === null && (
          <Input
            label="Matricola"
            placeholder="Es. UB120004"
            hint="Non ancora nota. Una volta salvata non si cambia più: tutto lo storico ci punterà."
            value={matricola}
            onChange={(e) => setMatricola(e.target.value)}
          />
        )}

        {errore && <p className="text-sm font-medium text-accent">{errore}</p>}
      </form>
    </Modal>
  );
}
